import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';

import { myColleaguesFilterFields } from '@features/filter/static/filterUtil';
import FilterComponent from '@features/filter/v2/FilterComponent';
import MyColleaguesDetails from '@features/auth/components/MyColleaguesDetails';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';
import { showMyColleaguesDetailsPage } from '@store/actions/MyColleaguesAction';
import { useMyColleagues } from '@store/reducers';
import { Types } from '@store/types';

import ApiClient from '@lib/apiClient';

import { COUNTRY_CODES, printApiError } from '@utils/Util';

const MyColleagues = () => {
	const [colleagueList, setColleagueList] = useState([]);
	const [detailsUserId, setDetailsUserId] = useState(null);
	const dispatch = useDispatch();
	const { searchKey, isShowFilter } = useMyColleagues();
	const [selectedCountryDetails, setSelectedCountryDetails] = useState({
		id: 'GLOBAL',
		name: 'GLOBAL',
		code: 'GLOBAL'
	});

	const filterFields = useSelector((store) => store.filterFieldStore);
	const [countryList, setCountryList] = useState([]);
	const [showDetailsPage, setShowDetailsPage] = useState(false);

	useEffect(() => {
		dispatch(showMyColleaguesDetailsPage(showDetailsPage));
	}, [showDetailsPage]);

	useEffect(() => {
		const filterParams = generateFilterParams(filterFields);
		Promise.all([
			getCountryWise(filterParams),
			fetchMyColleaguesFilterOptions(filterParams),
			getAllColleagueList(filterParams)
		]);
	}, [filterFields, searchKey, selectedCountryDetails.id]);

	const renameFilterFieldsKey = (key) => {
		const fieldMapping = {
			stackholderIds: 'textTagIdList',
			designationIds: 'designationList',
			myColleaguesDepartment: 'departmentIdList',
			teamLeadIds: 'teamLeadIdList'
		};
		return fieldMapping[key] || key;
	};

	const generateFilterParams = (selectedFilterFields = {}) => {
		let params = '';
		Object.keys(selectedFilterFields).forEach((key) => {
			const fields = selectedFilterFields[key];
			if (Array.isArray(fields) && fields.length > 0) {
				const values = fields
					.filter((item) => !item.key.toString().startsWith('ALL'))
					.map((field) => {
						if (field?.name === 'stackholderIds') {
							return field?.value;
						} else if (field.name === 'designationIds') {
							return field.value;
						} else {
							return field?.key;
						}
					});
				if (params.length > 0) {
					params += '&';
				}
				const paramKey = renameFilterFieldsKey(key);
				params += `${paramKey}=${values.join(',')}`;
			}
		});

		params += selectedCountryDetails?.id !== 'GLOBAL' ? `&countryId=${selectedCountryDetails?.id}` : '';
		return `?${params}${searchKey !== '' ? `&search=${searchKey}` : ''}`;
	};

	const fetchMyColleaguesFilterOptions = async (params) => {
		try {
			const { data } = await ApiClient.GET('myColleaguesFilterOptions', params);
			dispatch({
				type: Types.STORE_DEPENDENT_FILTER_LIST,
				payload: { responseData: data, filterListPage: 'MY_COLLEAGUES' }
			});
		} catch (error) {
			printApiError(error);
		}
	};

	const getCountryWise = async (params) => {
		try {
			const { data = [] } = await ApiClient.GET('myColleaguesSummary', params);
			const response = await data.map((item) => {
				if (item?.country?.code === 'GLOBAL') {
					return {
						...item,
						country: {
							...item?.country,
							id: item?.country?.code
						}
					};
				} else {
					return item;
				}
			});
			setCountryList(response);
		} catch (error) {
			printApiError(error);
		}
	};

	const getAllColleagueList = async (params) => {
		dispatch(openLoader());
		try {
			const { data } = await ApiClient.GET('getColleagues', params);
			setColleagueList(data);
		} catch {
			toastError('Error getting colleague list');
		} finally {
			dispatch(closeLoader());
		}
	};

	const getProfilePicUrl = (colleague) => {
		return colleague.profilePicDocument?.docUrl || `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`;
	};

	const handleRowClick = (e, id) => {
		e.preventDefault();
		setShowDetailsPage(true);
		setDetailsUserId(id);
	};

	const onBackButtonClick = (e) => {
		e.preventDefault();
		setShowDetailsPage(false);
		setDetailsUserId(null);
	};

	const onAddToClipBoard = (text) => {
		navigator.clipboard
			.writeText(text)
			.then(() => {
				toastSuccess('Copied successfully');
			})
			.catch(() => {
				toastError('Something went wrong!');
			});
	};
	const getCountryFlag = (code) => {
		const flag = COUNTRY_CODES.find((country) => country.code === code);
		return flag?.image;
	};

	const renderTable = (userList) => {
		return (
			<div className="scroll-x-label">
				<table className="table table-colleage with-border">
					<thead>
						<tr>
							<th className="weight-500">Name</th>
							<th className="weight-500">Team Lead</th>
							<th className="weight-500">Designation</th>
							<th className="weight-500">Phone</th>
							<th className="weight-500">Email</th>
							<th className="weight-500">Responsibility</th>
						</tr>
					</thead>
					{userList?.map((colleague) => {
						const flagUrl = getCountryFlag(colleague?.country?.code);
						return (
							<tr key={colleague.id} onClick={(e) => handleRowClick(e, colleague.id)}>
								<td className="name-and-flag">
									<div className=" d-flex justify-content-start align-items-center">
										<img className="profile-img mr-1" src={getProfilePicUrl(colleague)} />
										<span>{colleague?.name}</span>
										{selectedCountryDetails?.id === 'GLOBAL' && (
											<span>
												{' '}
												<img
													className="flag ml-2"
													src={flagUrl}
													alt={colleague?.country?.name}
												/>
											</span>
										)}
									</div>
								</td>
								<td className="teamLead">
									{colleague?.teamLeadList?.map((teamLead, index) => (
										<div
											key={`${teamLead?.id}-${index}`}
											className="team-lead-single d-flex justify-content-start align-items-center"
										>
											<img
												className="profile-img lead-img mr-2"
												src={
													teamLead?.profilePicDocument?.docUrl
														? teamLead?.profilePicDocument?.docUrl
														: `${process.env.PUBLIC_URL}/images/pro_pic_default.svg`
												}
												alt={teamLead?.name}
											/>
											<p className="regular-14 gray_dark_02">{teamLead?.name}</p>
										</div>
									))}
								</td>
								<td className="designation">
									<span>{colleague?.designation}</span>
								</td>
								<td className="email phone-no">
									<p>
										{colleague?.phone}{' '}
										<span
											onClick={(e) => {
												e.stopPropagation();
												onAddToClipBoard(colleague?.phone);
											}}
										>
											<img
												src={`${process.env.PUBLIC_URL}/icons/copy-gray.svg`}
												alt=""
											/>
										</span>
									</p>
								</td>
								<td className="email">
									<p>
										{colleague?.email}
										<span
											onClick={(e) => {
												e.stopPropagation();
												onAddToClipBoard(colleague?.email);
											}}
										>
											<img
												src={`${process.env.PUBLIC_URL}/icons/copy-gray.svg`}
												alt=""
											/>
										</span>
									</p>
								</td>
								<td className="response-description">
									<p>{colleague?.responsibility}</p>
								</td>
							</tr>
						);
					})}
				</table>
			</div>
		);
	};

	const renderAccordionWrapper = (item, table, index) => {
		return (
			<div className="card">
				<div className="card-header" id={`heading${index}`}>
					<h2 className="mb-0">
						<button
							className="btn btn-link btn-block d-flex align-items-center justify-content-between text-left gray_dark_02 regular-12 weight-600 collapsed"
							type="button"
							data-toggle="collapse"
							data-target={`#collapse${index}`}
							aria-expanded="false"
							aria-controls={`collapse${index}`}
						>
							{item?.department.toUpperCase()}{' '}
							{item?.userList.length > 0 && `(${item?.userList.length})`}
							<div className="arrow-down">
								<img
									src={`${process.env.PUBLIC_URL}/icons/down-arrow-gray-dark-02.svg`}
									alt="arrow"
								/>
							</div>
						</button>
					</h2>
				</div>

				<div id={`collapse${index}`} className="collapse" aria-labelledby={`heading${index}`}>
					<div className="card-body p-0">{table && table(item?.userList)}</div>
				</div>
			</div>
		);
	};

	const renderCountryWiseFilterCard = (country) => {
		const flagUrl = getCountryFlag(country?.country?.code);
		return (
			<div
				className={`single-country-details ${
					selectedCountryDetails?.id === country?.country?.id ? 'active' : ''
				}`}
				onClick={() => setSelectedCountryDetails(country?.country)}
			>
				<p className="regular-14 gray_dark_02 weight-500 mb-2">
					<img src={flagUrl} alt={country?.country?.name} className="mr-1" />
					{country?.country?.name}
				</p>
				<div className="value-info">
					<p className="regular-12 gray_dark_02">
						<span>Department:</span>
						<span className="semibold-16 gray_dark_02 count">{country?.noOfDepartment}</span>
					</p>
					<p className="regular-12 gray_dark_02">
						<span>People:</span>
						<span className="semibold-16 gray_dark_02 count">{country?.noOfPeople}</span>
					</p>
				</div>
			</div>
		);
	};

	const renderMyColleagues = () => {
		return (
			<div className="wraper container-fluid px-0 dashboard-container" style={{ paddingTop: '0px' }}>
				<section className="filter-data-container filter-v3-data-container">
					<FilterComponent
						filterPageName={'MY_COLLEAGUES'}
						fields={myColleaguesFilterFields}
						showFilter={isShowFilter}
						isDependentFilter={true}
					/>
					<div className="data-table overflow-hidden">
						<div className="country-wise-breakdown colleagues-page pb-2 d-flex scroll-x-label">
							{countryList.map((country) => (
								<React.Fragment key={country?.id}>
									{renderCountryWiseFilterCard(country)}
								</React.Fragment>
							))}
						</div>
						<div className="colleagues-accordion common-accordion">
							<div className="accordion" id="accordionExample">
								{colleagueList.map((item, index) => (
									<React.Fragment key={`${item?.department}-index-${index}`}>
										{renderAccordionWrapper(item, renderTable, index)}
									</React.Fragment>
								))}
							</div>
						</div>
					</div>
				</section>
			</div>
		);
	};

	const renderMyColleagueDetails = () => (
		<MyColleaguesDetails id={detailsUserId} onBackButtonClick={onBackButtonClick} />
	);

	return (
		<>
			{!showDetailsPage && renderMyColleagues()}
			{showDetailsPage && renderMyColleagueDetails()}
		</>
	);
};

export default MyColleagues;
